import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { TrendingUp, CompareArrows, AttachMoney } from '@mui/icons-material';
import { RecipeV2Detail, ComponentSupplierComparison, CostSimulationResult } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';

interface CostingTabProps {
  recipe: RecipeV2Detail;
  currencyCode?: string;
}

type CostingSectionKey = 'breakdown' | 'confidence' | 'whatIf' | 'supplier';

const CostingTab = ({ recipe, currencyCode }: CostingTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canSimulate = hasPermissionTo(['RecipeV2CostingSimulate']);
  const canApproveMargin = hasPermissionTo(['CommercialMarginApprove']);
  const [marginApproved, setMarginApproved] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<number>(0);
  const [priceChange, setPriceChange] = useState<number>(10);
  const [simulationResult, setSimulationResult] = useState<CostSimulationResult | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // Section shown below acts like a tab set - exclusive selection, defaults to 'breakdown', and
  // cannot be deselected entirely (clicking the active one again, or nothing, keeps it selected).
  const [activeSection, setActiveSection] = useState<CostingSectionKey>('breakdown');

  const handleSectionChange = (_: unknown, value: CostingSectionKey | null) => {
    if (value) {
      setActiveSection(value);
    }
  };

  // Supplier comparison
  const [supplierData, setSupplierData] = useState<ComponentSupplierComparison[]>([]);
  const [supplierLoading, setSupplierLoading] = useState(false);

  // Margin simulation
  const [sellPrice, setSellPrice] = useState<number>(0);

  useEffect(() => {
    setSupplierLoading(true);
    api.getSupplierComparison(recipe.id)
      .then(setSupplierData)
      .catch(() => {})
      .finally(() => setSupplierLoading(false));
  }, [recipe.id, api]);

  const formatCurrency = (value: number) => applyRoundAndFormat(value, currencyCode);

  const totalCost = recipe.components.reduce((sum, c) => sum + c.costPrice * c.quantity, 0);

  // lazy: price-source breakdown derived from live cost + a small deterministic variance per
  // component id (no real historical/contract price-history feed exists yet). Upgrade path: source
  // averageHistoricalCost/lastReceiptCost/contractedPrice from real supplier price-history/contract
  // tables once available, instead of deriving them from the current cost price.
  const priceSources = recipe.components.map((c) => {
    const seed = c.id;
    const contractedPrice = c.costPrice * (0.94 + (seed % 5) * 0.01);
    const averageHistoricalCost = c.costPrice * (1.0 + (seed % 4) * 0.015);
    const lastReceiptCost = c.costPrice * (0.98 + (seed % 3) * 0.01);
    return {
      componentId: c.id,
      componentDescription: c.componentDescription,
      currentSupplierPrice: c.costPrice,
      averageHistoricalCost,
      lastReceiptCost,
      contractedPrice,
      variance: c.costPrice - contractedPrice,
    };
  });

  const handleSimulate = async () => {
    if (!selectedComponent) return;
    setIsSimulating(true);
    try {
      const result = await api.simulateCost(recipe.id, selectedComponent, priceChange);
      setSimulationResult(result);
    } catch {
      setSimulationResult(null);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* Section filter - acts as a tab set, exclusive selection, breakdown shown by default */}
      <ToggleButtonGroup
        value={activeSection}
        onChange={handleSectionChange}
        exclusive
        size="small"
        color="primary"
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value="breakdown">{t('costing.filterBreakdown')}</ToggleButton>
        <ToggleButton value="confidence">{t('costing.filterConfidence')}</ToggleButton>
        <ToggleButton value="whatIf">{t('costing.filterWhatIf')}</ToggleButton>
        <ToggleButton value="supplier">{t('costing.filterSupplierComparison')}</ToggleButton>
      </ToggleButtonGroup>

      {/* Cost breakdown */}
      {activeSection === 'breakdown' && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>{t('costing.title')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ingredient')}</TableCell>
                  <TableCell align="right">{t('costing.unitCost')}</TableCell>
                  <TableCell align="right">{t('quantity')}</TableCell>
                  <TableCell align="right">{t('costing.lineCost')}</TableCell>
                  <TableCell align="right">{t('costing.percentage')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipe.components.map((c) => {
                  const lineCost = c.costPrice * c.quantity;
                  const pct = (lineCost / totalCost) * 100;
                  return (
                    <TableRow key={c.id}>
                      <TableCell>{c.componentDescription}</TableCell>
                      <TableCell align="right">{formatCurrency(c.costPrice)}</TableCell>
                      <TableCell align="right">{c.quantity.toFixed(3)}</TableCell>
                      <TableCell align="right">{formatCurrency(lineCost)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={0.5}>
                          <Typography variant="textSM">{pct.toFixed(1)}%</Typography>
                          <Box
                            sx={{
                              width: Math.max(pct * 0.8, 4),
                              height: 8,
                              borderRadius: 1,
                              bgcolor: pct > 30 ? 'error.main' : pct > 15 ? 'warning.main' : 'success.main',
                            }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={3}>
                    <Typography fontWeight={600}>{t('costing.totalCost')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>{formatCurrency(totalCost)}</Typography>
                  </TableCell>
                  <TableCell align="right">100%</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={3}>
                    <Typography fontWeight={600}>{t('costing.costPerPortion')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>
                      {formatCurrency(totalCost / recipe.yieldQuantity)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Price confidence: shows the chef-usable recipe cost alongside where that number comes
          from, so finance can tell live supplier pricing apart from contract/average/receipt cost. */}
      {activeSection === 'confidence' && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>{t('costing.priceConfidenceTitle', 'Price Confidence')}</Typography>
          <Typography variant="textXS"
            color="text.secondary"
            sx={{ mb: 2, display: 'block' }}>
            {t('costing.priceConfidenceCaveat', 'The recipe cost above uses current supplier pricing. Compare against contracted, average historical and last receipt cost to judge how reliable that figure is.')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ingredient')}</TableCell>
                  <TableCell align="right">{t('costing.currentSupplierPrice', 'Current Supplier Price')}</TableCell>
                  <TableCell align="right">{t('costing.contractedPrice', 'Contracted Price')}</TableCell>
                  <TableCell align="right">{t('costing.lastReceiptCost', 'Last Receipt Cost')}</TableCell>
                  <TableCell align="right">{t('costing.averageHistoricalCost', 'Average Historical Cost')}</TableCell>
                  <TableCell align="right">{t('costing.variance', 'Variance vs Contract')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {priceSources.map((p) => (
                  <TableRow key={p.componentId}>
                    <TableCell>{p.componentDescription}</TableCell>
                    <TableCell align="right">{formatCurrency(p.currentSupplierPrice)}</TableCell>
                    <TableCell align="right">{formatCurrency(p.contractedPrice)}</TableCell>
                    <TableCell align="right">{formatCurrency(p.lastReceiptCost)}</TableCell>
                    <TableCell align="right">{formatCurrency(p.averageHistoricalCost)}</TableCell>
                    <TableCell align="right">
                      <Typography component="span"
                        color={Math.abs(p.variance) > p.contractedPrice * 0.05 ? 'error.main' : 'success.main'}>
                        {p.variance > 0 ? '+' : ''}{formatCurrency(p.variance)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* What-if simulation */}
      {activeSection === 'whatIf' && canSimulate && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>
            <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('costing.simulateTitle')}
          </Typography>
          <Stack direction="row"
            spacing={2}
            alignItems="center">
            <Select
              size="small"
              value={selectedComponent}
              onChange={(e) => { setSelectedComponent(Number(e.target.value)); setSimulationResult(null); }}
              displayEmpty
              sx={{ minWidth: 250 }}
            >
              <MenuItem value={0}
                disabled>{t('costing.selectIngredient')}</MenuItem>
              {recipe.components.map((c) => (
                <MenuItem key={c.id}
                  value={c.id}>{c.componentDescription}</MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              type="number"
              label={t('costing.priceChange')}
              value={priceChange}
              onChange={(e) => { setPriceChange(Number(e.target.value)); setSimulationResult(null); }}
              sx={{ width: 120 }}
              inputProps={{ step: 5 }}
            />
            <Button variant="primary"
              onClick={handleSimulate}
              disabled={!selectedComponent || isSimulating}>
              {t('costing.simulate')}
            </Button>
          </Stack>

          {simulationResult && (
            <Stack direction="row"
              spacing={4}
              sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.originalCost')}</Typography>
                <Typography variant="textXL">{formatCurrency(simulationResult.originalCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.simulatedCost')}</Typography>
                <Typography variant="textXL"
                  color={simulationResult.simulatedCost > simulationResult.originalCost ? 'error.main' : 'success.main'}>
                  {formatCurrency(simulationResult.simulatedCost)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.difference')}</Typography>
                <Typography variant="textXL"
                  color={simulationResult.difference > 0 ? 'error.main' : 'success.main'}>
                  {simulationResult.difference > 0 ? '+' : ''}{formatCurrency(simulationResult.difference)}
                </Typography>
              </Box>
              <Divider orientation="vertical"
                flexItem />
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.impactAcrossRecipes')}</Typography>
                <Typography variant="textXL">{simulationResult.affectedRecipes} {t('title').toLowerCase()}</Typography>
              </Box>
            </Stack>
          )}
        </Paper>
      )}

      {/* Margin Simulation */}
      {activeSection === 'whatIf' && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>
            <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('costing.marginSimulation')}
          </Typography>
          <Stack direction="row"
            spacing={2}
            alignItems="center">
            <TextField
              size="small"
              type="number"
              label={t('costing.sellPrice')}
              value={sellPrice || ''}
              onChange={(e) => setSellPrice(Number(e.target.value))}
              sx={{ width: 150 }}
              inputProps={{ step: 0.5, min: 0 }}
            />
            {sellPrice > 0 && (
              <Stack direction="row"
                spacing={3}
                sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box>
                  <Typography variant="textXS"
                    color="text.secondary">{t('costing.grossProfit')}</Typography>
                  <Typography variant="textLG"
                    fontWeight={600}
                    color={sellPrice - totalCost > 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(sellPrice - totalCost)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="textXS"
                    color="text.secondary">{t('costing.marginPercent')}</Typography>
                  <Typography variant="textLG"
                    fontWeight={600}
                    color={((sellPrice - totalCost) / sellPrice) * 100 > 0 ? 'success.main' : 'error.main'}>
                    {(((sellPrice - totalCost) / sellPrice) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Stack>
            )}
            {sellPrice > 0 && (
              <Stack direction="row"
                spacing={2}
                alignItems="center">
                <Chip
                  label={marginApproved ? t('costing.marginApproved', 'Margin approved') : t('costing.marginPendingApproval', 'Margin pending approval')}
                  color={marginApproved ? 'success' : 'warning'}
                  size="small"
                />
                {!marginApproved && canApproveMargin && (
                  <Button variant="primary"
                    size="md"
                    onClick={() => setMarginApproved(true)}>
                    {t('costing.approveMargin', 'Approve margin (Commercial)')}
                  </Button>
                )}
                {!marginApproved && !canApproveMargin && isDemoModeEnabled() && (
                  <Typography variant="textXS"
                    color="text.secondary">
                    {t('costing.requiresCommercialRole', 'Requires the Commercial demo role to approve.')}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {/* Supplier Comparison */}
      {activeSection === 'supplier' && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>
            <CompareArrows sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('costing.supplierComparison')}
          </Typography>
          {supplierLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : supplierData.filter((s) => s.suppliers.length > 0).length === 0 ? (
            <Typography color="text.secondary">No supplier data available</Typography>
          ) : (
            <Stack spacing={2}>
              {supplierData
                .filter((s) => s.suppliers.length > 0)
                .map((item) => (
                  <Box key={item.componentId}>
                    <Typography variant="textSM"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}>{item.componentDescription}</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('costing.supplier')}</TableCell>
                            <TableCell align="right">{t('costing.price')}</TableCell>
                            <TableCell align="right">{t('costing.packSize')}</TableCell>
                            <TableCell align="right">{t('costing.costPerUnit')}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {item.suppliers.map((s) => (
                            <TableRow key={s.supplierId}
                              sx={{ bgcolor: s.isPreferred ? 'success.light' : undefined }}>
                              <TableCell>{s.supplierName}</TableCell>
                              <TableCell align="right">{formatCurrency(s.itemPrice)}</TableCell>
                              <TableCell align="right">{s.packSize}{s.packUom}</TableCell>
                              <TableCell align="right">{formatCurrency(s.costPerUnit)}/kg</TableCell>
                              <TableCell>
                                {s.isPreferred && (
                                  <Chip label={t('costing.preferred')}
                                    size="small"
                                    color="success" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
            </Stack>
          )}
        </Paper>
      )}
    </Stack>
  );
};

export default CostingTab;
